import { useState } from "react";
import BrandMark from "./BrandMark";

interface Props {
  onSignIn: (email: string, password: string) => { ok: true } | { ok: false; error: string };
}

// Demo convenience: the buttons below fill the credential fields on click so a
// walkthrough audience can sign in without typing. Fields start empty. Remove
// this when the production Entra ID / MSAL.js path replaces the hardcoded gate.
// Mirrors the ACCOUNTS map in lib/auth.ts.
const DEMO_ACCOUNTS = [
  { label: "CFO · enterprise", email: "demo@cashflowsentinel.com", password: "sentinel2026" },
  { label: "Viewer · read-only", email: "viewer@cashflowsentinel.com", password: "viewer" },
];

export default function LoginScreen({ onSignIn }: Props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fillDemo = (acct: (typeof DEMO_ACCOUNTS)[number]) => {
    setError(null);
    setEmail(acct.email);
    setPassword(acct.password);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    // Tiny delay so the spinner is visible — feels less abrupt
    setTimeout(() => {
      const r = onSignIn(email, password);
      setLoading(false);
      if (!r.ok) setError(r.error);
    }, 320);
  };

  return (
    <div className="login-screen">
      <div className="login-bg" aria-hidden />
      <div className="login-card">
        <div className="login-brand">
          <BrandMark size={48} />
          <div className="login-brand-text">
            <div className="login-brand-title">Cashflow Sentinel</div>
            <div className="login-brand-sub">Portfolio cash intelligence</div>
          </div>
        </div>

        <h1 className="login-heading">Sign in</h1>
        <p className="login-sub">Enterprise cash intelligence — Azure-native, explainable AI.</p>

        <button type="button" className="login-sso" disabled title="Production wires this to Entra ID via MSAL.js">
          <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden>
            <rect x="0" y="0" width="7" height="7" fill="#f25022" />
            <rect x="9" y="0" width="7" height="7" fill="#7fba00" />
            <rect x="0" y="9" width="7" height="7" fill="#00a4ef" />
            <rect x="9" y="9" width="7" height="7" fill="#ffb900" />
          </svg>
          Sign in with Microsoft
          <span className="login-sso-tag">Entra ID · production</span>
        </button>

        <div className="login-divider"><span>or with credentials</span></div>

        <form className="login-form" onSubmit={submit}>
          <label className="login-field">
            <span>Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              autoComplete="username"
              required
              autoFocus
            />
          </label>
          <label className="login-field">
            <span>Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              required
            />
          </label>

          {error && <div className="login-error">{error}</div>}

          <button type="submit" className="login-submit" disabled={loading}>
            {loading ? <span className="spinner" style={{ width: 14, height: 14 }} /> : "Sign in →"}
          </button>

          <div className="login-demo-row">
            <span className="login-demo-label muted">Use demo account:</span>
            {DEMO_ACCOUNTS.map((acct) => (
              <button
                key={acct.email}
                type="button"
                className="login-demo-fill"
                onClick={() => fillDemo(acct)}
                disabled={loading}
              >
                {acct.label}
              </button>
            ))}
          </div>
        </form>

        <div className="login-foot">
          <span className="muted">Cashflow Sentinel · v2026.18</span>
        </div>
      </div>
    </div>
  );
}
